import styles from './edit-license-dialog.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { useContext, useState } from 'react';
import { useImmerReducer } from 'use-immer';
import { useGetRequestNewHook } from '../requestUtils';
import {
  NumberSelect, IContractLicense, IContractItem, ISellableProduct, calcNextInvoiceCost, calcOneTimeDiscountFromTotalPrice, calcRecurringDiscountFromTotalPrice, calcRecurringPrice,
  ContractStateContext, CreateHeaders, Dialog, DialogActions, DialogContent, DialogTitle, HeaderSizeEnum, MenuItem, PrimaryButton, reducer, Select, selectedItem, textHeader,
  useSingleContractDispatch, licenseHasChanges, ContractOrderTypesEnum, TextField, GrayOutlinedButton, SnackBarContext, IContractStateType, IOrderItem, DatePicker
} from '@frontend-workspace/react-shared-lib';
import WebkuaTable from '../webkua-table/webkua-table';
import { DateSelectionEnum } from '../_enums/date-selection';
import { useTranslation } from 'react-i18next';


/* eslint-disable-next-line */
export interface EditLicenseDialogProps {
  licenseNumber: number,
  items: IContractItem[],
  onClose: () => void,
  dispatch: any,
  contract: IContractStateType
}

export function EditLicenseDialogContent(props: EditLicenseDialogProps) {
  const [contract, dispatch] = useImmerReducer<IContractStateType>(reducer, props.contract);
  const orderItems = contract.orderItems.filter((i: IOrderItem) => i.licenseNumber === props.licenseNumber && i.type === ContractOrderTypesEnum.AddProductsToContract);
  const allItems = props.items.concat(orderItems);
  const { data: sellableProducts }: { data: Array<ISellableProduct> } = useGetRequestNewHook(`contracts/${contract.contractNumber}/action/getSellableProducts/${allItems[0].vismaContractItemId}`, [])
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();

  const isNewItem = (id: string) => !!contract.orderItems.find((i: IOrderItem) => i.id === id && i.type === ContractOrderTypesEnum.AddProductsToContract);
  const terminateItem = contract.orderItems.find((i: IOrderItem) => i.licenseNumber === props.licenseNumber && i.type === ContractOrderTypesEnum.TerminateLicense);

  const quantityRenderer = (params: ValueFormatterParams) => {
    const seatChange = contract.orderItems.find((i: IOrderItem) => i.contractItemId === params.data.id && i.type === ContractOrderTypesEnum.AddSeats);
    const currentValue = seatChange ? params.value + seatChange.quantity : params.value;
    return (
      <div>{currentValue}</div>
    );
  };

  const licensesWithExistingChanges = contract.orderItems.filter(i => i.isExisting).map(i => i.licenseNumber);
  const hasAnyFutureChanges = props.items.some(i => licensesWithExistingChanges.includes(i.licenseNumber));

  const changeRenderer = (params: ValueFormatterParams) => {
    const seatChange = contract.orderItems.find((i: IOrderItem) => i.contractItemId === params.data.id && i.type === ContractOrderTypesEnum.AddSeats);
    const quantity = params.value;
    const currentValue = seatChange ? seatChange.quantity : isNewItem(params.data.id) ? params.value : 0;

    const onChange = (value: number) => {
      isNewItem(params.data.id) ?
        dispatch({ type: "NEW_ITEM_SEATS_CHANGE", licenseNumber: props.licenseNumber, newValue: value, orderId: params.data.id }) :
        dispatch({ type: "SEATS_CHANGE", licenseNumber: props.licenseNumber, contractItemId: params.data.id, newValue: quantity + value });
    };
    return (
      <NumberSelect
        value={currentValue}
        onValueChange={onChange}
        supportNegative
      />
    );
  };

  const DiscountPercentRenderer = (params: ValueFormatterParams) => {
    let currentValue = params.value;
    const discountChange = contract.orderItems.find((i: IOrderItem) => i.contractItemId === params.data.id && i.type === ContractOrderTypesEnum.ChangeRecurringDiscountPercent);
    currentValue = discountChange ? discountChange.recurringDiscountPercent : currentValue;
    const [value, setValue] = useState(currentValue);
    const onBlur = () => isNewItem(params.data.id) ?
      dispatch({ type: "NEW_ITEM_RECURRING_DISCOUNT_CHANGE", licenseNumber: params.data.licenseNumber, orderId: params.data.id, newValue: value }) :
      dispatch({ type: "DISCOUNT_CHANGE", licenseNumber: params.data.licenseNumber, contractItemId: params.data.id, newValue: value });
    const onChange = (event: any) => setValue(Number(event.target.value));
    return (
      <div className='d-flex align-items-center'>
        <TextField
          type="number"
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={event => { event.target.select() }}
          sx={{ width: 100 }}
        />
      </div>
    );
  };

  const SalePriceRenderer = (params: ValueFormatterParams) => {
    let currentValue = isNewItem(params.data.id) ? calcNextInvoiceCost(contract.contractInfo, params.data, params.data.oneTimeDiscountAmount, contract.newSaleDate) : 0;
    const seatChange = contract.orderItems.find((i: IOrderItem) => i.contractItemId === params.data.id && i.type === ContractOrderTypesEnum.AddSeats);
    currentValue = seatChange ? currentValue + calcNextInvoiceCost(contract.contractInfo, seatChange, seatChange.oneTimeDiscountAmount, contract.newSaleDate) : currentValue;
    const [value, setValue] = useState<number>(currentValue);
    const onChange = (event: any) => setValue(Number(event.target.value));
    const onBlur = () => seatChange ? dispatch({
      type: "NEW_SEATS_ONE_TIME_DISCOUNT_CHANGE",
      licenseNumber: props.licenseNumber,
      orderId: seatChange.id,
      newValue: calcOneTimeDiscountFromTotalPrice(contract, seatChange, value)
    }) : dispatch({
      type: "NEW_ITEM_ONE_TIME_DISCOUNT_CHANGE",
      licenseNumber: props.licenseNumber,
      orderId: params.data.id,
      newValue: calcOneTimeDiscountFromTotalPrice(contract, params.data, value)
    });
    return (
      <div className='d-flex align-items-center'>
        <TextField
          type="number"
          value={value.toFixed()}
          onBlur={onBlur}
          onChange={onChange}
          disabled={!isNewItem(params.data.id) && !seatChange}
          onFocus={event => { event.target.select() }}
        />
      </div>
    );
  };

  const OneTimeDiscountRenderer = (params: ValueFormatterParams) => {
    const seatChange = contract.orderItems.find((i: IOrderItem) => i.contractItemId === params.data.id && i.type === ContractOrderTypesEnum.AddSeats);
    const currentValue = seatChange ? seatChange.oneTimeDiscountAmount : params.value;
    const [value, setValue] = useState(currentValue || 0);
    const onChange = (value: any) => setValue(Number(value.currentTarget.value));
    const onBlur = () => seatChange ? dispatch({
      type: "NEW_SEATS_ONE_TIME_DISCOUNT_CHANGE",
      licenseNumber: props.licenseNumber,
      orderId: seatChange.id,
      newValue: value
    }) : dispatch({
      type: "NEW_ITEM_ONE_TIME_DISCOUNT_CHANGE",
      licenseNumber: props.licenseNumber,
      orderId: params.data.id,
      newValue: value
    })
    return (
      <div className='d-flex align-items-center'>
        <TextField
          type="number"
          value={value.toFixed()}
          onChange={onChange}
          onBlur={onBlur}
          disabled={!isNewItem(params.data.id) && !seatChange}
          onFocus={event => { event.target.select() }}
        />
      </div>
    );
  };

  const RecurringPriceRenderer = (params: ValueFormatterParams) => {
    const contractItem: IContractItem = params.data;
    const seatChange = contract.orderItems.find((i: IOrderItem) => i.contractItemId === contractItem.id && i.type === ContractOrderTypesEnum.AddSeats);
    const discountChange = contract.orderItems.find((i: IOrderItem) => i.contractItemId === contractItem.id && i.type === ContractOrderTypesEnum.ChangeRecurringDiscountPercent);
    const quantity = seatChange ? contractItem.quantity + seatChange.quantity : contractItem.quantity;
    const recurringDiscountPercent = discountChange ? discountChange.recurringDiscountPercent : contractItem.recurringDiscountPercent;
    const recurringPrice = calcRecurringPrice(quantity, contractItem.recurringPrice, recurringDiscountPercent);
    const [value, setValue] = useState<number>(recurringPrice);
    const onChange = (value: any) => setValue(Number(value.currentTarget.value));
    const newDiscount = calcRecurringDiscountFromTotalPrice(contractItem.quantity, contractItem.recurringPrice, value);
    const onBlur = () => isNewItem(params.data.id) ?
      dispatch({ type: "NEW_ITEM_RECURRING_DISCOUNT_CHANGE", licenseNumber: contractItem.licenseNumber, orderId: params.data.id, newValue: newDiscount }) :
      dispatch({ type: "DISCOUNT_CHANGE", licenseNumber: contractItem.licenseNumber, contractItemId: contractItem.id, newValue: newDiscount });
    return (
      <div className='d-flex align-items-center'>
        <TextField
          type="number"
          value={value.toFixed()}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={event => { event.target.select() }}
        />
      </div>
    );
  };

  const productNameRenderer = (params: ValueFormatterParams) => {
    const licenseNumber = params.data.licenseNumber;
    const hasFutureChanges = licensesWithExistingChanges.includes(licenseNumber);
    return (
      `${params.value} ${hasFutureChanges ? "*" : ""}`
    );
  };

  const handleEditLicense = () => {
    props.dispatch({ type: "LICENSE_SAVE_CHANGES", contract: contract });
    setSnackbarMessage({ content: t("editLicense.licenseChangesSaved"), type: "success" });
    props.onClose();
  };

  const handleNewItem = (event: any) => {
    const vismaId = event.target.value;
    const product = sellableProducts.find(p => p.vismaId === vismaId);
    if (product) {
      const item: selectedItem = {
        licenseType: product.licenseType,
        price: product.price,
        isModule: product.isModule,
        productName: product.productName,
        vismaId: vismaId,
        quantity: 1,
        groupId: product.groupId
      };
      dispatch({ type: "NEW_LICENSE_ITEM_CREATED", item: item, licenseNumber: props.licenseNumber });
    }
  };

  const handleTerminateChange = (date: Date | null) => dispatch({ type: "LICENSE_TERMINATION_CHANGE", licenseNumber: props.licenseNumber, newDate: date });
  const handleSaleDateChange = (date: Date | null) => dispatch({ type: "SALE_ACTIVATION_DATE_CHANGE", newDate: date });

  const [dateSelection, setDateSelection] = useState(DateSelectionEnum.None);
  const dateSelections = [
    { id: DateSelectionEnum.None, name: '\u00A0' },
    { id: DateSelectionEnum.Now, name: t("editLicense.endNow") },
    { id: DateSelectionEnum.Next, name: t("editLicense.endAtTheNextDueDate") },
    { id: DateSelectionEnum.Custom, name: t("editLicense.custom") },
  ];

  const handleDateSelection = (event: any) => {
    const dateSelection: DateSelectionEnum = event.target.value;
    setDateSelection(dateSelection);
    if (dateSelection === DateSelectionEnum.Now) handleTerminateChange(new Date())
    else if (dateSelection === DateSelectionEnum.Next) handleTerminateChange(new Date(props.contract.contractInfo.nextInvoicingDate))
  };

  return (
    <Dialog
      open
      maxWidth={'xxl'}
      fullWidth>
      <DialogTitle className='mb-4'>
        {t("standard.changeLicense")}: <span className='MuiTypography-body3'>{props.licenseNumber}</span>
      </DialogTitle>
      <DialogContent>
        <div className="d-flex flex-row flex-wrap mb-0 mb-lg-3">
          <div className='mb-3 mb-lg-0 me-3'>
            <Select
              fullWidth
              sx={{ width: '260px' }}
              label={t("editLicense.addNewItem")}
              name='sellableProducts'
              onChange={handleNewItem}
              value={''}>
              {sellableProducts.map(p => <MenuItem value={p.vismaId} key={p.vismaId}>{p.productName}</MenuItem>)}
            </Select>
          </div>
          <div className='mb-3 mb-lg-0 me-3'>
            <DatePicker
              inputFormat="dd.MM.yyyy"
              label={t("editLicense.dateOfSale")}
              disabled={!licenseHasChanges(contract, props.licenseNumber)}
              value={contract.newSaleDate}
              onChange={handleSaleDateChange}
            />
          </div>
          <div className='mb-3 mb-lg-0 me-3'>
            <Select
              fullWidth
              sx={{ width: '260px' }}
              label={t("editLicense.terminateLicense")}
              name='dateSelection'
              onChange={handleDateSelection}
              value={dateSelection}>
              {dateSelections.map(d => <MenuItem value={d.id} key={d.id}>{d.name}</MenuItem>)}
            </Select>
          </div>
          {dateSelection === DateSelectionEnum.Custom &&
            <div className='mb-3 mb-lg-0'>
              <DatePicker
                inputFormat="dd.MM.yyyy"
                label={t("standard.terminationDate")}
                value={terminateItem ? terminateItem.activationDate : null}
                onChange={handleTerminateChange}
              />
            </div>
          }
        </div>
        <WebkuaTable
          headers={CreateHeaders([
            textHeader("productName", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE, { renderer: productNameRenderer }),
            textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.MEDIUM, { renderer: productNameRenderer }),
            textHeader("quantity", t("editLicense.change"), HeaderSizeEnum.MEDIUM, { renderer: changeRenderer }),
            textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.MEDIUM, {
              renderer: quantityRenderer,
              cellStyle: { justifyContent: 'right' },
              headerClass: "ag-right-aligned-header"
            }),
            textHeader("recurringDiscountPercent", t("standard.recurringDiscountPercent"), HeaderSizeEnum.MEDIUM, { renderer: DiscountPercentRenderer }),
            textHeader("oneTimeDiscountAmount", t("standard.oneTimeDiscount"), HeaderSizeEnum.MEDIUM, { renderer: OneTimeDiscountRenderer }),
            textHeader("", t("editLicense.salePrice"), HeaderSizeEnum.MEDIUM, { renderer: SalePriceRenderer }),
            textHeader("itemPrice", t("standard.renewalPrice"), HeaderSizeEnum.MEDIUM, { renderer: RecurringPriceRenderer }),
          ])}
          data={allItems}
          disableSearch
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant='contained'
          size='large'
          className='minButtonWidth'
          disabled={hasAnyFutureChanges}
          onClick={handleEditLicense}>
          {hasAnyFutureChanges ? t("editLicense.hasFutureChanges") : 'OK'}
        </PrimaryButton>
        <GrayOutlinedButton
          onClick={props.onClose}
          size='large'
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export function EditLicenseDialog(props: {
  licenseNumber: number,
  onClose: () => void
}) {
  const contract = useContext(ContractStateContext);
  const dispatch = useSingleContractDispatch();

  if (!contract) return null;
  const licenses = contract.groups.reduce((licenses: Array<IContractLicense>, group) => licenses.concat(group.licenses), []);
  const initialLicense = licenses.find(l => l.licenseNumber === props.licenseNumber);
  const licenseItems = initialLicense ? initialLicense?.modules.reduce((allItems, m) => allItems.concat(m.items), initialLicense.items) : [];
  if (!props.licenseNumber && props.licenseNumber !== 0) return null;

  return (
    <EditLicenseDialogContent
      onClose={props.onClose}
      dispatch={dispatch}
      items={licenseItems}
      licenseNumber={props.licenseNumber}
      contract={contract}
    />
  );
};

export default EditLicenseDialog;